/**
 * Utilitários para trabalhar com JWT (JSON Web Tokens)
 * Decodificação e validação sem dependências externas
 */

export interface JWTPayload {
  userId?: string;
  email?: string;
  exp?: number; // Timestamp de expiração em segundos
  iat?: number; // Timestamp de emissão em segundos
  [key: string]: any;
}

/**
 * Decodifica um token JWT e retorna o payload
 * @param token - Token JWT completo
 * @returns Payload decodificado ou null se inválido
 */
export function decodeToken(token: string): JWTPayload | null {
  try {
    // JWT tem formato: header.payload.signature
    const parts = token.split('.');
    
    if (parts.length !== 3) {
      console.error('Token JWT inválido: formato incorreto');
      return null;
    }
    
    // Decodifica a parte do payload (segunda parte)
    const payload = parts[1];
    
    // Substitui caracteres URL-safe para base64 padrão
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    
    // Decodifica base64
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Erro ao decodificar token JWT:', error);
    return null;
  }
}

/**
 * Verifica se um token JWT está expirado
 * @param token - Token JWT completo
 * @returns true se o token estiver expirado, false caso contrário
 */
export function isTokenExpired(token: string): boolean {
  const payload = decodeToken(token);
  
  if (!payload) {
    return true; // Token inválido é considerado expirado
  }
  
  if (!payload.exp) {
    // Se não tem campo exp, consideramos válido (sem expiração)
    return false;
  }
  
  // exp está em segundos, Date.now() está em milissegundos
  const now = Math.floor(Date.now() / 1000);
  
  return payload.exp < now;
}

/**
 * Obtém o tempo restante até a expiração do token em segundos
 * @param token - Token JWT completo
 * @returns Segundos até expiração, ou null se token inválido/sem expiração
 */
export function getTokenExpiryTime(token: string): number | null {
  const payload = decodeToken(token);
  
  if (!payload || !payload.exp) {
    return null;
  }
  
  const now = Math.floor(Date.now() / 1000);
  const timeLeft = payload.exp - now;
  
  return timeLeft > 0 ? timeLeft : 0;
}

/**
 * Valida se um token tem o formato básico correto de JWT
 * @param token - String a ser validada
 * @returns true se tem formato válido de JWT
 */
export function isValidJWTFormat(token: string): boolean {
  if (!token || typeof token !== 'string') {
    return false;
  }
  
  const parts = token.split('.');
  return parts.length === 3 && parts.every(part => part.length > 0);
}
